import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import { KpiCard, DashboardHeader, SectionTable } from "../components/DashboardWidgets";
import { Package, ArrowsCounterClockwise, XCircle, Truck, CheckCircle, Warning } from "@phosphor-icons/react";

const KPI = [
  { key: "total_products",        label: "Total Products",        icon: Package,                tone: "slate" },
  { key: "in_stock_count",        label: "In Stock",              icon: CheckCircle,            tone: "emerald" },
  { key: "low_stock_count",       label: "Low Stock",             icon: Warning,                tone: "amber" },
  { key: "out_of_stock_count",    label: "Out of Stock",          icon: XCircle,                tone: "rose" },
  { key: "total_orders",          label: "Sales Orders",          icon: ArrowsCounterClockwise, tone: "blue" },
  { key: "pending_dispatch_count",label: "Pending Dispatch",      icon: Truck,                  tone: "amber" },
  { key: "dispatched_count",      label: "Dispatched",            icon: CheckCircle,            tone: "emerald" },
];

const StoreDashboard = ({ user, logout }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/dashboard/store`); setData(r.data); }
    catch { toast.error("Failed to load Store dashboard"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar user={user} logout={logout} />
      <div className="flex-1 ml-64">
        <div className="p-8">
          <DashboardHeader overline="Store & Delivery" title="Store Dashboard" subtitle="Stock health, low-stock alerts, dispatch pipeline." onRefresh={load} refreshTestId="store-refresh-btn" />
          {loading ? <div className="flex items-center justify-center py-20"><div className="loading-spinner"></div></div> : (
            <>
              <div data-testid="store-kpi-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-10">
                {KPI.map(k => <KpiCard key={k.key} icon={k.icon} label={k.label} tone={k.tone} money={k.money} value={data?.kpis?.[k.key]} testId={`store-kpi-${k.key}`} />)}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <SectionTable
                  title="Low Stock Items" icon={Warning} testId="store-low-stock"
                  columns={[{ label: "Product" }, { label: "Category" }, { label: "Stock", align: "right" }]}
                  rows={data?.low_stock_items || []}
                  emptyText="No low-stock items."
                  renderRow={(r, i) => (
                    <tr key={r.product_id} data-testid={`store-low-row-${i}`} className="border-b border-slate-100">
                      <td className="p-3 font-medium">{r.name}</td>
                      <td className="p-3 text-xs text-slate-600 uppercase tracking-wider">{r.category}</td>
                      <td className="p-3 text-right tabular-nums text-amber-700 font-semibold">{r.stock}</td>
                    </tr>
                  )}
                />
                <SectionTable
                  title="Out of Stock" icon={XCircle} testId="store-out-of-stock"
                  columns={[{ label: "Product" }, { label: "Category" }]}
                  rows={data?.out_of_stock_items || []}
                  emptyText="Everything in stock."
                  renderRow={(r, i) => (
                    <tr key={r.product_id} data-testid={`store-oos-row-${i}`} className="border-b border-slate-100">
                      <td className="p-3 font-medium">{r.name}</td>
                      <td className="p-3 text-xs text-slate-600 uppercase tracking-wider">{r.category}</td>
                    </tr>
                  )}
                />
              </div>
              <SectionTable
                title="Pending Dispatch" icon={Truck} testId="store-pending-dispatch"
                columns={[{ label: "SO #" }, { label: "Customer" }, { label: "Expected Delivery" }, { label: "Status" }]}
                rows={data?.pending_dispatch || []}
                emptyText="Nothing waiting to dispatch."
                renderRow={(r, i) => (
                  <tr key={`${r.so_number}-${i}`} data-testid={`store-dispatch-row-${i}`} className="border-b border-slate-100">
                    <td className="p-3 font-mono text-xs">{r.so_number || "—"}</td>
                    <td className="p-3">{r.customer_name}</td>
                    <td className="p-3 text-sm text-slate-600">{r.expected_delivery_date || "—"}</td>
                    <td className="p-3 text-xs uppercase tracking-wider">{r.status}</td>
                  </tr>
                )}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StoreDashboard;
